import React from 'react';
import { Lucide } from '@/chassis/ui/components';
import { ApiError } from './http-client';

export type ValidationIssue = { field: string; message: string };

const extractDetailMessage = (details: any): string | null => {
    if (!details) return null;
    if (typeof details === 'string') return details;
    if (Array.isArray(details)) {
        for (const entry of details) {
            if (!entry) continue;
            if (typeof entry === 'string') return entry;
            if (typeof entry.message === 'string') return entry.message;
            if (Array.isArray(entry.message)) {
                const msg = entry.message.find((m: any) => typeof m === 'string');
                if (msg) return msg;
            }
            const constraints = entry.constraints || entry.messages;
            if (constraints && typeof constraints === 'object') {
                const constraintValues = Object.values(constraints).filter((v) => typeof v === 'string');
                if (constraintValues.length) return constraintValues[0] as string;
            }
        }
    }
    if (typeof details === 'object') {
        if (typeof details.message === 'string') return details.message;
        if (Array.isArray(details.message)) {
            const msg = details.message.find((m: any) => typeof m === 'string');
            if (msg) return msg;
        }
        const nested = details.errors || details.details || details.validationErrors;
        if (nested) return extractDetailMessage(nested);
        const firstValue = Object.values(details).find((v) => typeof v === 'string');
        if (typeof firstValue === 'string') return firstValue;
    }
    return null;
};

const collectValidationIssues = (details: any, parentPath = ''): ValidationIssue[] => {
    if (!details) return [];
    const pathPrefix = parentPath ? `${parentPath}.` : '';

    if (Array.isArray(details)) {
        return details.flatMap((entry) => {
            if (typeof entry === 'string') return [{ field: parentPath || '(field)', message: entry }];
            return collectValidationIssues(entry, parentPath);
        });
    }

    if (typeof details === 'object') {
        const issues: ValidationIssue[] = [];
        const property = typeof details.property === 'string' ? details.property : undefined;
        const fieldPath = property ? `${pathPrefix}${property}` : parentPath;

        const constraints = details.constraints || details.messages;
        if (constraints && typeof constraints === 'object') {
            Object.values(constraints).forEach((msg) => {
                if (typeof msg === 'string') issues.push({ field: fieldPath || '(field)', message: msg });
            });
        }

        if (typeof details.message === 'string') {
            issues.push({ field: fieldPath || '(field)', message: details.message });
        }

        if (Array.isArray(details.message)) {
            details.message.forEach((msg: any) => {
                if (typeof msg === 'string') issues.push({ field: fieldPath || '(field)', message: msg });
            });
        }

        const children = details.children || details.errors || details.validationErrors;
        if (Array.isArray(children) || typeof children === 'object') {
            issues.push(...collectValidationIssues(children, fieldPath));
        }

        return issues;
    }

    return [];
};

const summarizeApplicationException = (details: any): { message: string; details?: string[] } | null => {
    if (!details) return null;
    const container = details.details || details;
    if (!container || typeof container !== 'object') return null;

    const missingFixed = Array.isArray(container.missingFixedRequiredFields) ? container.missingFixedRequiredFields : [];
    const missingConditional = Array.isArray(container.missingConditionalRequiredFields)
        ? container.missingConditionalRequiredFields
        : [];

    const detailLines: string[] = [];
    missingFixed.forEach((field: any) => detailLines.push(`Campo obrigatório ausente: ${field}`));
    missingConditional.forEach((field: any) => detailLines.push(`Campo condicional ausente: ${field}`));

    if (typeof container.message === 'string') detailLines.push(container.message);
    if (Array.isArray(container.message)) {
        const msgs = container.message.filter((m: any) => typeof m === 'string');
        if (msgs.length) detailLines.push(...msgs);
    }

    return detailLines.length ? { message: 'Dados inválidos', details: detailLines } : null;
};

const resolveApplicationExceptionFromUnknown = (error: unknown): { message: string; details?: string[] } | null => {
    if (!error || typeof error !== 'object') return null;

    const asAny = error as any;
    const candidate = asAny.details?.details || asAny.details || asAny;
    return summarizeApplicationException(candidate);
};

const resolveSuccessFalse = (error: any, fallback: string): { message: string; details?: string[] } | null => {
    if (!error || typeof error !== 'object' || error.success !== false) return null;

    const appSummary = resolveApplicationExceptionFromUnknown(error);
    if (appSummary) return appSummary;

    const rawDetails = error.details;
    const details = Array.isArray(rawDetails)
        ? rawDetails.filter((d) => typeof d === 'string')
        : typeof rawDetails === 'string'
            ? [rawDetails]
            : undefined;

    const message = typeof error.message === 'string' && error.message.length ? error.message : fallback;
    return { message, details };
};

export const resolveApiErrorParts = (error: unknown, fallback: string): { message: string; details?: string[] } => {
    // Backend do CBCB retorna `errorMessage` com a mensagem de negocio descritiva
    // (ex: "Art. 164 — Prazo expirado"), mantendo `message` com o generico do NestJS
    // ("Internal server error" ou "Bad Request"). Priorizamos errorMessage quando presente.
    const probeErrorMessage = (value: any): string | null => {
        if (!value || typeof value !== 'object') return null;
        const candidates = [value.errorMessage, value.details?.errorMessage, value.details?.details?.errorMessage];
        for (const cand of candidates) {
            if (typeof cand === 'string' && cand.trim().length > 0) return cand.trim();
        }
        return null;
    };
    const businessMessage = probeErrorMessage(error);
    if (businessMessage) return { message: businessMessage };

    const successFalse = resolveSuccessFalse(error as any, fallback);
    if (successFalse) return successFalse;

    if (error && typeof error === 'object' && 'message' in (error as any) && typeof (error as any).message === 'string') {
        const maybeDetails = (error as any).details;
        const details = Array.isArray(maybeDetails) ? maybeDetails.filter((d) => typeof d === 'string') : undefined;
        const appSummary = resolveApplicationExceptionFromUnknown(error);
        if (appSummary) return appSummary;
        return { message: (error as any).message, details };
    }

    if (error instanceof ApiError) {
        const appSummary = summarizeApplicationException(error.details);
        if (appSummary) {
            return { message: appSummary.message, details: appSummary.details };
        }

        const validationIssues = collectValidationIssues(error.details);
        if (validationIssues.length) {
            const details = validationIssues.map((issue) => `${issue.field}: ${issue.message}`).filter(Boolean);
            const message = 'Erros de validação encontrados';
            return { message, details };
        }

        const detail = extractDetailMessage(error.details);
        const parts = [error.message, detail].filter(Boolean) as string[];

        if (parts.length === 1 && error.details) {
            parts.push(`Detalhes: ${JSON.stringify(error.details)}`);
        }

        if (parts.length) {
            const [message, ...rest] = parts;
            const details = rest.length ? rest : undefined;
            return { message, details };
        }

        const rawDetails = error.details ? `Detalhes: ${JSON.stringify(error.details)}` : null;
        return { message: rawDetails || fallback };
    }

    if (error instanceof Error) return { message: error.message || fallback };

    return { message: fallback };
};

export const resolveApiErrorMessage = (error: unknown, fallback: string) => resolveApiErrorParts(error, fallback).message;

export const renderApiErrorAlert = (error: unknown, fallback: string): React.ReactNode => {
    const { message, details } = resolveApiErrorParts(error, fallback);

    const icon = React.createElement(Lucide, {
        key: 'icon',
        icon: 'AlertTriangle',
        className: 'h-5 w-5 flex-shrink-0 text-orange-600',
        'aria-hidden': true,
    });

    const content = React.createElement(
        'div',
        { className: 'space-y-1' },
        [
            React.createElement('div', { key: 'msg', className: 'font-medium' }, message),
            details && details.length
                ? React.createElement(
                      'ul',
                      { key: 'list', className: 'list-disc space-y-0.5 pl-4' },
                      details.map((item, index) => React.createElement('li', { key: `${index}-${item}` }, item)),
                  )
                : null,
        ],
    );

    return React.createElement(
        'div',
        {
            className:
                'flex items-start gap-2 rounded-md border border-orange-200 bg-orange-50 p-3 text-sm text-orange-800',
        },
        [icon, content],
    );
};
